import {
  createRouter,
  createWebHistory,
  createWebHashHistory
} from "vue-router";

import { routes } from "./routes.js";

import envVars from "@/helpers/Env";
import eventHub from "@/helpers/utility/Events";

import type { NavigationGuard, SimcisionRouteMeta } from "./routeTypes";
import type { RouteLocationNormalized, RouteLocationNormalizedLoaded, RouteRecordNormalized } from "vue-router";

export const router = createRouter({
  history: envVars.isElectron
    ? createWebHashHistory()
    : createWebHistory(import.meta.env.BASE_URL),
  routes: routes,
  linkActiveClass: "is-active"
});

let isNavigating = false;

type RouteExtensions = {
  allGuards?: NavigationGuard[];
  leaveHooks?: NavigationGuard[];
  meta?: SimcisionRouteMeta;
};

router.beforeEach(
  async (
    to: RouteLocationNormalized & RouteExtensions,
    from: RouteLocationNormalized & RouteExtensions
  ) => {
    isNavigating = true;
    console.log(
      "%cRouting to:",
      "background-color: beige; display: inline-block; padding: 2px 5px; font-weight: 700;",
      to.path,
      from?.path
    );
    const oldGuards = from?.allGuards ?? [];

    to.leaveHooks = flattenGuards(to, "onLeave");
    to.allGuards = flattenGuards(to, "guards");

    // check LEAVE guards
    if (oldGuards.length > 0) {
      const leaveGuards = (from.allGuards || []).filter(
        (guard) => guard.leave && !(to.allGuards || []).includes(guard)
      );
      for (const guard of leaveGuards) {
        if (!guard.leave) continue;
        await guard.leave(to, from);
      }
    }

    // check ENTER and UPDATE guards
    for (const guard of to.allGuards) {
      if (guard.enter && !oldGuards.includes(guard)) {
        const redirect = await guard.enter(to, from);
        if (typeof redirect != "undefined" && redirect != true) {
          return redirect;
        }
      }
      if (guard.update) {
        const redirect = await guard.update(to, from);
        if (typeof redirect != "undefined" && redirect != true) {
          return redirect;
        }
      }
    }

    updateDocumentTitle(to);
  }
);
router.afterEach(() => {
  isNavigating = false;
});

async function reloadNavigationGuards() {
  const currentRoute = router.currentRoute
    .value as RouteLocationNormalizedLoaded & RouteExtensions;
  const allGuards = (currentRoute?.allGuards || []).slice();
  if (allGuards.length == 0) return true;

  // check enter/update guards

  let _redirect = null;
  for (const guard of allGuards) {
    if (guard.enter) {
      const redirect = await guard.enter(currentRoute, currentRoute);
      if (typeof redirect != "undefined" && redirect != true) {
        _redirect = redirect;
        break;
      }
    }
    if (guard.update) {
      const redirect = await guard.update(currentRoute, currentRoute);
      if (typeof redirect != "undefined" && redirect != true) {
        _redirect = redirect;
        break;
      }
    }
  }

  if (_redirect === false) {
    if (currentRoute.redirectedFrom) {
      return router.push(currentRoute.redirectedFrom);
    } else {
      return router.back();
    }
  }

  if (_redirect != null) {
    return router.push(_redirect);
  }
}
eventHub.on("user-changed", () => {
  if (!isNavigating) return;
  reloadNavigationGuards;
});

eventHub.on("route-change-request", (route) => {
  return router.push(route);
});

eventHub.on("model-route-update", (route) => {
  if (router.currentRoute.value.params.modelId != route.params.modelId) {
    return router.push(route);
  }
});

/**
 * Returns ALL the guards of a route, including the guards of any parents.
 */
function flattenGuards(
  route: RouteLocationNormalized & RouteExtensions,
  prop = "guards"
) {
  return (route.matched as (RouteRecordNormalized & RouteExtensions)[]).reduce(
    (guards, level) =>
      guards.concat((level?.meta?.[prop] || []) as NavigationGuard[]),
    [] as NavigationGuard[]
  );
}

/**
 * Sets the title of the document depending on the meta setting of the route
 */
async function updateDocumentTitle(
  route: RouteLocationNormalized & RouteExtensions
) {
  const titleMeta = route.meta?.title;
  if (titleMeta) {
    let title = "";
    if (typeof titleMeta === "function") {
      title = (await titleMeta(route)) || "";
    } else {
      title = titleMeta;
    }
    title += " | simcision";
    document.title = title;
  } else {
    document.title = "simcision";
  }
}

export function switchToEditorArea(area: string) {
  const areas = ["model", "variables", "interventions", "evaluation", "inspector"];
  if (areas.includes(area)) {
    const newPath = router.currentRoute.value.path.replace(
      new RegExp(`/(${areas.join("|")})(?=/|$)`),
      `/${area}`
    );
    router.push(newPath);
  } else {
    console.warn(`Tried to switch to unknown editor area: ${area}`, router.currentRoute.value.path);
  }
}

export default router;
